/**
 * `/progress` — one job progress event, as the executor will post it to
 * `POST /v1/jobs/progress` and `get_job` will hand it back in `timeline`
 * (D25, D26, #301). The schema ships now so the executor's seam (`/executor`)
 * can be typed against it; the `job_event` table and the routes are phase 3.
 *
 * Two levels (D26), told apart by `level` and by a closed `kind` on each:
 *
 *  - `system` — derived by the pipeline and the tool host from what actually
 *    happened (a file written, a build run, a checkpoint committed), never
 *    from the agent's words. Migration also emits platform progress milestones.
 *  - `agent` — the agent's own `report_progress(text)` milestone, always
 *    `kind: "progress"`, written for the site owner in the request's language
 *    and safe to show verbatim (`current_activity` is this level only).
 *
 * An agent cannot claim a platform event such as a build passed. Progress
 * can come from either level; the level identifies its source.
 */
import { z } from "zod";

/** Every family carries a version — expand/contract across one release tag (#290 §5). */
export const version = 1 as const;

export const SYSTEM_EVENT_KINDS = [
  "file_edited",
  "file_deleted",
  "build_started",
  "build_passed",
  "build_failed",
  "screenshot",
  "checkpoint",
  "push",
  "cancel_requested",
  "migration_capture",
  "migration_replica",
  "migration_chunk",
  "migration_verdict",
  "progress",
] as const;
export type SystemEventKind = (typeof SYSTEM_EVENT_KINDS)[number];

export const AGENT_EVENT_KINDS = ["progress"] as const;
export type AgentEventKind = (typeof AGENT_EVENT_KINDS)[number];

export const EVENT_LEVELS = ["system", "agent"] as const;
export type EventLevel = (typeof EVENT_LEVELS)[number];

/** `report_progress` is linted to this on the executor side; the schema refuses longer so the lint cannot be skipped. */
export const AGENT_EVENT_TEXT_MAX = 200;

const base = {
  /** When it happened, ISO 8601 with offset — the executor's clock, ordering is `seq` on the route (#301), not this. */
  ts: z.iso.datetime({ offset: true }),
  text: z.string(),
  /** Structured detail for `system` kinds (a path, a duration, a commit) — never shown verbatim. */
  data: z.record(z.string(), z.unknown()).optional(),
};

export const systemEventSchema = z.object({
  ...base,
  level: z.literal("system"),
  kind: z.enum(SYSTEM_EVENT_KINDS),
});

export const agentEventSchema = z.object({
  ...base,
  level: z.literal("agent"),
  kind: z.enum(AGENT_EVENT_KINDS),
  text: z.string().min(1).max(AGENT_EVENT_TEXT_MAX),
});

export const jobEventSchema = z.discriminatedUnion("level", [systemEventSchema, agentEventSchema]);
export type JobEvent = z.infer<typeof jobEventSchema>;
export type SystemEvent = z.infer<typeof systemEventSchema>;
export type AgentEvent = z.infer<typeof agentEventSchema>;
